import { useMemo, useState } from "react";
import { Search, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import AdminAvatar from "../../components/admin/AdminAvatar";
import { useAdminUsersQuery } from "../../api/query/useAdminQueries";
import { formatNaira } from "../../data/adminUsers";

const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  { value: "recent", label: "Last activity" },
  { value: "name", label: "Name" },
  { value: "bookings", label: "Total bookings" },
  { value: "spending", label: "Total spending" },
];

const USER_STATUS_STYLES = {
  Active: "bg-[#E4F5E4] text-[#008000]",
  Suspended: "bg-[#FFF4DB] text-[#F99C07]",
};

function formatLastActivity(isoString) {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB");
}

function AdminUsers() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [sortOpen, setSortOpen] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAdminUsersQuery(
    { search: searchQuery.trim() || undefined, page, limit: PAGE_SIZE }
  );

  const users = useMemo(() => {
    const list = [...(data?.data?.items || [])];
    if (sortBy === "name") {
      list.sort((a, b) => a.fullName.localeCompare(b.fullName));
    } else if (sortBy === "bookings") {
      list.sort((a, b) => b.totalBookings - a.totalBookings);
    } else if (sortBy === "spending") {
      list.sort((a, b) => b.totalSpending - a.totalSpending);
    }
    return list;
  }, [data, sortBy]);

  const totalResults = data?.data?.pagination?.total || 0;
  const totalPages = data?.data?.pagination?.totalPages || 1;
  const currentPage = page;
  const showingFrom = totalResults === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(currentPage * PAGE_SIZE, totalResults);

  const handleSearch = (value) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleSort = (value) => {
    setSortBy(value);
    setSortOpen(false);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
            Manage registered users, verify credentials and monitor performance rating
            and trips.
          </p>
        </div>

        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <h2 className="text-2xl font-bold text-gray-900">All Customers</h2>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-[280px]">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full rounded-full border border-[#C9D3E2] bg-white py-3 pl-12 pr-4 text-sm font-medium text-gray-700 outline-none transition focus:border-[#570033] focus:ring-2 focus:ring-[#570033]/10"
                />
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setSortOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between gap-2 rounded-full border border-[#C9D3E2] bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:border-[#570033] sm:w-auto"
                >
                  Sort by
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>
                {sortOpen ? (
                  <div className="absolute right-0 z-20 mt-2 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-lg">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleSort(option.value)}
                        className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition hover:bg-gray-50 ${
                          sortBy === option.value
                            ? "font-semibold text-[#570033]"
                            : "text-gray-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Users Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Email Address
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Location
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Total Bookings
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Total Spending
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Last Activity
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-sm text-gray-500">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr
                      key={user.id}
                      className="cursor-pointer border-b border-gray-100 transition hover:bg-gray-50"
                      onClick={() =>
                        navigate(`/admin/users/${user.id}`)
                      }
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <AdminAvatar
                            src={user.profilePicture}
                            name={user.fullName}
                          />
                          <span className="text-sm font-medium text-gray-900">
                            {user.fullName}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-600">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        N/A
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {user.totalBookings}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {formatNaira(user.totalSpending)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-600">
                        {formatLastActivity(user.lastActivity)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            USER_STATUS_STYLES[user.status] ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {user.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-500">
              Showing {showingFrom} to {showingTo} of {totalResults} results
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                (pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => setPage(pageNumber)}
                    className={`flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition ${
                      pageNumber === currentPage
                        ? "bg-[#570033] text-white"
                        : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                )
              )}

              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}

export default AdminUsers;
